/**
 * Access-control TREE — the source of truth for the admin Access page editor.
 *
 * It mirrors the admin UI: SECTION → PAGE → (sub-tab | action button). Each node
 * maps to backend permission code(s); the tree is pure data, the enforcement is
 * the existing `requirePerm` (perms live in the JWT, applied on next sign-in).
 *
 * Node kinds:
 *  - `section`  — a sidebar group; no perm, just a heading with children.
 *  - `page`     — a sidebar leaf/route. `perm` is its nav/view perm; `requires`
 *                 are the EXTRA read perms its load endpoints need, auto-granted
 *                 when the page is enabled (fixes the bug where enabling a page
 *                 granted view.members.users but not the user.read that GET /users
 *                 actually requires).
 *  - `tab`      — a sub-tab on a detail page. `perm` set + `lockable:true` = a real
 *                 toggle (distinct authority). `lockable:false` = informational
 *                 (shares the page's authority, e.g. a slot's genealogy views).
 *  - `button`   — an action. Always lockable; `perm` is the action's authority.
 *
 * The Member-detail and Slot-detail pages are the exception: EVERY tab there is
 * independently lockable (its own `*.detail.*.read` perm) so a role can be scoped
 * down to specific tabs. These detail-tab locks are enforced on the admin frontend
 * (the underlying genealogy/slot endpoints stay member-shared); elsewhere locks are
 * minted only at genuine authority boundaries to keep the catalog + JWT lean.
 */
export type AccessNodeKind = 'section' | 'page' | 'tab' | 'button';

export type AccessNode = {
  key: string;
  label: string;
  kind: AccessNodeKind;
  /** The permission this node grants (page nav perm / tab / button authority). */
  perm?: string;
  /** PAGE nodes only: extra read perms auto-granted when the page is enabled. */
  requires?: string[];
  /** tab/button: false = informational (no toggle, shares the page's authority). */
  lockable?: boolean;
  children?: AccessNode[];
};

/**
 * Export-format permission codes — the ONE place these strings are written.
 *
 * GLOBAL and ADVISORY, exactly like `consignment.print` / `consignment.export`:
 * every file is generated in the BROWSER from a `ReportResult` the surface's own
 * read perm already returned, so unchecking one hides an affordance — it does
 * NOT stop anyone from getting the data. Never present them as a security
 * boundary, and never gate a route on them.
 *
 * They are global rather than per-page on purpose: "this role may take data out
 * as a spreadsheet" is one decision, and minting 3 codes per report surface
 * would bloat every JWT for no extra authority. A page's own view perm is what
 * decides WHICH data is exportable.
 *
 * The Consignment surfaces are the deliberate exception: they keep honouring
 * `consignment.export` for all three formats (grandfathered), so a tenant that
 * already narrowed consignment exports is not silently re-opened.
 */
export const EXPORT_PERMS = {
  csv: 'export.csv',
  excel: 'export.excel',
  pdf: 'export.pdf',
} as const;

/** The three codes as a list (seed + migration 0174 + the Access editor). */
export const EXPORT_FORMAT_PERMS = [
  EXPORT_PERMS.csv,
  EXPORT_PERMS.excel,
  EXPORT_PERMS.pdf,
] as const;

// ── builders (keep the literal tree terse + consistent) ────────────────────────
const page = (
  key: string,
  label: string,
  perm: string,
  opts: { requires?: string[]; children?: AccessNode[] } = {},
): AccessNode => ({
  kind: 'page',
  key,
  label,
  perm,
  requires: opts.requires,
  children: opts.children,
});

/** A lockable sub-tab (real backend boundary). */
const tab = (key: string, label: string, perm: string): AccessNode => ({
  kind: 'tab',
  key,
  label,
  perm,
  lockable: true,
});

/** An informational sub-tab — shares the page's authority, no separate toggle. */
const info = (key: string, label: string): AccessNode => ({
  kind: 'tab',
  key,
  label,
  lockable: false,
});

/** A lockable action button. */
const btn = (key: string, label: string, perm: string): AccessNode => ({
  kind: 'button',
  key,
  label,
  perm,
  lockable: true,
});

/**
 * The three per-format export buttons for one surface, keyed off the page's
 * node key. Every instance points at the SAME three global codes (see
 * `EXPORT_PERMS`) — the repeated nodes exist so the editor shows the operator
 * where the buttons live, not because each page has its own authority.
 */
const exportBtns = (pageKey: string): AccessNode[] => [
  btn(`${pageKey}.btn.export_csv`, 'Export to CSV', EXPORT_PERMS.csv),
  btn(`${pageKey}.btn.export_excel`, 'Export to Excel', EXPORT_PERMS.excel),
  btn(`${pageKey}.btn.export_pdf`, 'Export to PDF', EXPORT_PERMS.pdf),
];

const section = (key: string, label: string, children: AccessNode[]): AccessNode => ({
  kind: 'section',
  key,
  label,
  children,
});

export const ACCESS_TREE: AccessNode[] = [
  section('members', 'Members', [
    page('members.users', 'Users', 'view.members.users', {
      requires: ['user.read'], // GET /users requires user.read, not the nav perm
      children: [
        tab('members.users.tab.information', 'Information', 'member.detail.info.read'),
        tab('members.users.tab.slots', 'Slots', 'slot.read'),
        tab('members.users.tab.wallet', 'Wallet & Activity', 'member.wallet.read'),
        tab('members.users.tab.verification', 'Verification (KYC)', 'kyc.read'),
        tab('members.users.tab.beneficiary', 'Beneficiary', 'member.detail.beneficiary.read'),
        btn('members.users.btn.new', 'New / edit user', 'user.write'),
        btn('members.users.btn.pwreset', 'Reset password', 'user.password.reset'),
        btn('members.users.btn.adjust', 'Adjust wallet/points', 'member.balance.adjust'),
        btn('members.users.btn.kyc', 'Approve / reject KYC', 'kyc.review'),
      ],
    }),
    page('members.kyc', 'KYC Review', 'view.members.users', {
      requires: ['kyc.read'],
      children: [btn('members.kyc.btn.review', 'Approve / reject', 'kyc.review')],
    }),
    page('members.slots', 'Slots', 'view.members.slots', {
      requires: ['slot.read'], // GET /admin/slots requires slot.read, not the nav perm
      children: [
        tab('members.slots.tab.info', 'Information', 'slot.detail.info.read'),
        tab('members.slots.tab.walletsummary', 'Wallet + Breakdown', 'slot.detail.wallet.read'),
        // Cross-cutting visibility: when off, income figures (Slots-list wallet/
        // earned columns + member-detail summary amounts) are hidden for the role.
        tab('members.slots.tab.income', 'Income amounts (wallet & earned)', 'member.income.view'),
        tab('members.slots.tab.wallet', 'Wallet Logs', 'member.wallet.read'),
        tab('members.slots.tab.distributed', 'Distributed Income', 'distribution.read'),
        tab('members.slots.tab.unilevel', 'Unilevel Tree', 'slot.detail.unilevel.read'),
        tab('members.slots.tab.binary', 'Binary Tree', 'slot.detail.binary.read'),
        tab('members.slots.tab.points', 'Points History', 'slot.detail.points.read'),
        tab('members.slots.tab.inventory', 'Inventory', 'slot.detail.inventory.read'),
        tab('members.slots.tab.binaryhist', 'Binary History', 'slot.detail.binary_history.read'),
        btn('members.slots.btn.new', 'New slot', 'slot.create'),
        btn('members.slots.btn.adjust', 'Adjust wallet/points', 'member.balance.adjust'),
        btn('members.slots.btn.company', 'Mark/unmark company', 'slot.write'),
        btn('members.slots.btn.changeowner', 'Change owner', 'slot.write'),
        btn('members.slots.btn.transfersponsor', 'Transfer sponsor', 'slot.write'),
      ],
    }),
    page('members.memberships', 'Memberships', 'view.members.memberships', {
      // GET /memberships is public (member portal); only writes are gated.
      children: [btn('members.memberships.btn.write', 'Create / edit tiers', 'membership.write')],
    }),
    page('members.access', 'Access (roles)', 'view.members.access', {
      requires: ['user.roles.assign'], // the role builder loads via user.roles.assign
    }),
    // Phase 8 — the GDPR erasure door. Viewing shows the queue and its
    // pre-flight (wallet balance, open cash-outs, downline); the button is what
    // actually archives, and it is the ONLY route that can (the ordinary user
    // PATCH refuses an archived row and cannot set the status).
    page('members.archive', 'Archive requests', 'view.members.archive', {
      children: [btn('members.archive.btn.execute', 'Execute / reject archival', 'member.archive')],
    }),
  ]),

  section('commerce', 'Commerce', [
    page('commerce.orders', 'Orders', 'order.read', {
      children: [
        btn('commerce.orders.btn.status', 'Status / ship / cancel / refund', 'order.write'),
        btn('commerce.orders.btn.payment', 'Approve payment', 'payment.approve'),
      ],
    }),
    page('commerce.products', 'Products & Categories', 'view.products.catalog', {
      children: [
        btn('commerce.products.btn.write', 'Create / edit products & categories', 'product.write'),
      ],
    }),
    page('commerce.coupons', 'Coupons', 'view.products.coupons', {
      children: [btn('commerce.coupons.btn.write', 'Create / edit coupons', 'coupon.write')],
    }),
    page('commerce.suppliers', 'Suppliers', 'view.products.suppliers', {
      children: [btn('commerce.suppliers.btn.write', 'Create / edit suppliers', 'supplier.write')],
    }),
    page('commerce.purchase_orders', 'Purchase Orders', 'view.products.purchase_orders', {
      children: [
        btn('commerce.po.btn.write', 'Create / approve / cancel POs', 'purchase_order.write'),
      ],
    }),
    page('commerce.branches', 'Branches', 'view.branch.branches', {
      children: [
        info('commerce.branches.tab.cashiers', 'Cashiers'),
        info('commerce.branches.tab.inventory', 'Inventory & Logs'),
        btn(
          'commerce.branches.btn.write',
          'Create / edit branch, e-com, numbering, stock',
          'branch.write',
        ),
        btn('commerce.branches.btn.cashiers', 'Provision cashiers', 'cashier.provision'),
      ],
    }),
    page('commerce.shipping', 'Shipping', 'view.branch.shipping', {
      children: [btn('commerce.shipping.btn.write', 'Publish couriers / zones', 'shipping.write')],
    }),
    // ONE page node covering the three payment-config sidebar leaves (POS
    // payment methods, POS custom fees, Ecom payment methods) — they share a
    // view perm and one write authority (`tenant.payment_methods.write`, the
    // key-scoped features-PATCH slice mirroring `tenant.storefront.write`).
    page(
      'commerce.payment_methods',
      'Payment methods & POS fees',
      'view.commerce.payment_methods',
      {
        children: [
          btn(
            'commerce.payment_methods.btn.write',
            'Edit payment methods & POS fees',
            'tenant.payment_methods.write',
          ),
        ],
      },
    ),
    page('commerce.merchants', 'Merchants', 'view.merchants.accounts', {
      children: [
        info('commerce.merchants.tab.accounts', 'Accounts'),
        tab('commerce.merchants.tab.products', 'Products', 'view.merchants.products'),
        tab('commerce.merchants.tab.payouts', 'Payouts', 'view.merchants.payouts'),
        btn('commerce.merchants.btn.account', 'Approve / suspend account', 'merchant.approve'),
        btn(
          'commerce.merchants.btn.product',
          'Approve / reject product',
          'merchant.product.approve',
        ),
        btn('commerce.merchants.btn.payout', 'Process merchant payouts', 'merchant.payout.process'),
      ],
    }),
    page('commerce.creators', 'Creators', 'view.creators.accounts', {
      children: [
        info('commerce.creators.tab.accounts', 'Accounts'),
        tab('commerce.creators.tab.courses', 'Courses', 'view.creators.courses'),
        tab('commerce.creators.tab.payouts', 'Payouts', 'view.creators.payouts'),
        btn('commerce.creators.btn.account', 'Approve / suspend account', 'creator.approve'),
        btn('commerce.creators.btn.course', 'Approve / reject course', 'creator.course.approve'),
        btn('commerce.creators.btn.payout', 'Process creator payouts', 'creator.payout.process'),
      ],
    }),
    page('commerce.coaching', 'Coaching', 'view.coaching', {
      // The Members tab reads one member's plan through the ordinary admin user
      // read, so enabling the page must grant it like any other detail surface.
      requires: ['user.read'],
      children: [
        info('commerce.coaching.tab.plans', 'Plans'),
        info('commerce.coaching.tab.content', 'Content'),
        info('commerce.coaching.tab.members', 'Members'),
        info('commerce.coaching.tab.usage', 'Usage'),
        btn(
          'commerce.coaching.btn.plans',
          'Set the Pro / Elite plan products',
          'tenant.features.write',
        ),
        btn(
          'commerce.coaching.btn.content',
          'Edit affirmations, journal prompts and chart copy',
          'coaching.content.write',
        ),
        btn('commerce.coaching.btn.entitlement', 'Grant or revoke a member plan', 'user.write'),
      ],
    }),
  ]),

  /**
   * Cashier POS — the `pos.*` perms. These were Advanced-only for a long time,
   * which meant a tenant could not grant/revoke "may this cashier hand out a
   * free slot?" without hand-picking a raw permission code. They gate the
   * cashier SPA's nav + action buttons, not admin pages, but they are real
   * authority boundaries and belong in the editor like any other.
   */
  section('cashier', 'Cashier POS', [
    page('cashier.checkout', 'Checkout & Sales Orders', 'pos.checkout', {
      children: [
        btn('cashier.checkout.btn.fs', 'Activate free slot (FS)', 'pos.slot.activate'),
        btn('cashier.checkout.btn.refund', 'Void / refund a sale', 'pos.refund'),
        btn(
          'cashier.checkout.btn.memo',
          'Edit remarks after the fact (sale remarks, void/refund reason, shift notes)',
          'pos.memo.edit',
        ),
        btn(
          'cashier.checkout.btn.discount',
          'Approve above-cap discount (manager PIN)',
          'pos.discount.override',
        ),
      ],
    }),
    page('cashier.releasing', 'Releasing', 'pos.release'),
    page('cashier.orders', 'Orders', 'pos.orders.view', {
      children: [
        btn(
          'cashier.orders.btn.fulfill',
          'Advance fulfilment (ship / deliver / complete)',
          'pos.orders.fulfill',
        ),
      ],
    }),
    // Read-only: branch stock levels + the append-only movement log ("View
    // log"). Separate from admin's branch.write, which also grants receive/
    // write-off.
    page('cashier.inventory', 'Inventory & Logs', 'pos.inventory.view'),
    // Accounting v1 — the consignment surfaces (customers, consign-out,
    // sell-through billing, returns, receive payment, SOA). Hidden entirely
    // while the `consignment_enabled` tenant flag is off.
    // Each document type is its own authority (the single `consignment.write`
    // switch that shipped with Accounting v1 is retired). `print` / `export`
    // are ADVISORY: both are built client-side from payloads `consignment.read`
    // already returns, so there is nothing to enforce server-side — they exist
    // so a tenant can hide the affordances, NOT as a security boundary.
    page('cashier.consignment', 'Consignment', 'consignment.read', {
      children: [
        btn(
          'cashier.consignment.btn.consign_out',
          'Consign stock out to a consignee (Consignment Note)',
          'consignment.consign_out',
        ),
        btn(
          'cashier.consignment.btn.bill',
          'Record consignee sales (Billing Statement)',
          'consignment.bill',
        ),
        btn(
          'cashier.consignment.btn.receive_payment',
          'Receive payment against a billing (Payment Record)',
          'consignment.receive_payment',
        ),
        btn(
          'cashier.consignment.btn.return',
          'Record a return of unsold consigned stock',
          'consignment.return',
        ),
        btn(
          'cashier.consignment.btn.void',
          'Void any consignment document (reason required)',
          'consignment.void',
        ),
        btn(
          'cashier.consignment.btn.print',
          'Print consignment documents and statements',
          'consignment.print',
        ),
        btn(
          'cashier.consignment.btn.export',
          'Export consignment tabs to Excel / PDF',
          'consignment.export',
        ),
      ],
    }),
    // Track 2A — the branch-keyed stockist-consignment surfaces (consign-out,
    // auto-billed sell-through receivables, payments, returns). Hidden entirely
    // while the `stockist_consignment_enabled` tenant flag is off. Born
    // per-action (no legacy single switch); `print` / `export` are ADVISORY,
    // same rationale as the consignment pair above.
    page('cashier.stockist_consignment', 'Stockist Consignment', 'stockist_consignment.read', {
      children: [
        btn(
          'cashier.stockist_consignment.btn.consign_out',
          'Consign stock out to a stockist branch (Consignment Note)',
          'stockist_consignment.consign_out',
        ),
        btn(
          'cashier.stockist_consignment.btn.bill',
          'Record stockist sell-through billings (Billing Statement)',
          'stockist_consignment.bill',
        ),
        btn(
          'cashier.stockist_consignment.btn.receive_payment',
          'Receive payment against a stockist billing (Payment Record)',
          'stockist_consignment.receive_payment',
        ),
        btn(
          'cashier.stockist_consignment.btn.return',
          'Record a return of unsold stockist-consigned stock',
          'stockist_consignment.return',
        ),
        btn(
          'cashier.stockist_consignment.btn.void',
          'Void any stockist-consignment document (reason required)',
          'stockist_consignment.void',
        ),
        btn(
          'cashier.stockist_consignment.btn.print',
          'Print stockist-consignment documents and statements',
          'stockist_consignment.print',
        ),
        btn(
          'cashier.stockist_consignment.btn.export',
          'Export stockist-consignment tabs to Excel / PDF',
          'stockist_consignment.export',
        ),
      ],
    }),
  ]),

  section('messages', 'Messages & Feedback', [
    page('comms.messages', 'Messages', 'view.messages'),
    page('comms.feedback', 'Feedback', 'view.feedback'),
    // Phase 8 — member abuse reports over LMS content. Read and act are split
    // so a tenant can staff a moderator who triages without hiding anything.
    page('comms.moderation', 'Moderation', 'view.moderation', {
      children: [
        btn('comms.moderation.btn.act', 'Hide / resolve reported content', 'moderation.act'),
      ],
    }),
  ]),

  section('finance', 'Finance', [
    page('finance.payments', 'Payments', 'view.payments', {
      requires: ['payment.approve'], // the requests list loads via payment.approve
      children: [
        btn('finance.payments.btn.review', 'Verify / approve / reject', 'payment.approve'),
      ],
    }),
    page('finance.payouts', 'Payouts', 'view.payouts.requests', {
      requires: ['payout.approve'],
      children: [
        tab('finance.payouts.tab.batches', 'Batches', 'payout.process'),
        btn('finance.payouts.btn.process', 'Batch / mark paid / reject', 'payout.process'),
      ],
    }),
    page('finance.fees', 'Fees & Tax', 'view.payouts.fees', {
      children: [
        btn('finance.fees.btn.write', 'Edit methods & deductions', 'tenant.features.write'),
      ],
    }),
  ]),

  section('compensation', 'Compensation', [
    page('comp.plans', 'Plans', 'view.compensation.compplan', {
      children: [btn('comp.plans.btn.publish', 'Publish config', 'compplan.config.write')],
    }),
    page('comp.periods', 'Periods', 'view.compensation.periods', {
      children: [btn('comp.periods.btn.close', 'Close period', 'compplan.recompute')],
    }),
    page('comp.distribute', 'Distribution', 'view.compensation.distribute', {
      children: [
        btn(
          'comp.distribute.btn.run',
          'Activate / load / compute / distribute',
          'compplan.recompute',
        ),
      ],
    }),
    page('comp.distributions', 'Distributions', 'view.compensation.distributions', {
      requires: ['distribution.read'],
    }),
    page('comp.vortex', 'Vortex Monitor', 'view.compensation.vortex'),
  ]),

  section('storefront', 'Storefront', [
    page('storefront.content', 'Content Editor', 'view.settings.tenant', {
      // Storefront-CONTENT saves go through the grantable `tenant.storefront.write`
      // (a scoped slice of `tenant.features.write` — a features PATCH touching Fees
      // & Tax / settings / comp flags still needs the full super-admin-only perm).
      children: [
        btn('storefront.content.btn.write', 'Save storefront content', 'tenant.storefront.write'),
      ],
    }),
    page('storefront.landing_packages', 'Landing Packages', 'view.settings.tenant', {
      children: [
        btn(
          'storefront.landing_packages.btn.write',
          'Save landing packages',
          'tenant.storefront.write',
        ),
      ],
    }),
    page('storefront.featured_packages', 'Featured Packages', 'view.settings.tenant', {
      children: [
        btn(
          'storefront.featured_packages.btn.write',
          'Save featured packages',
          'tenant.storefront.write',
        ),
      ],
    }),
    page('storefront.feedback', 'Official Store Feedback', 'view.products.catalog', {
      children: [
        btn('storefront.feedback.btn.feature', 'Feature / unfeature review', 'review.feature'),
      ],
    }),
  ]),

  section('content', 'Content', [
    page('content.videos', 'Training Videos', 'view.assets.training_videos', {
      children: [
        btn('content.videos.btn.write', 'Create / edit videos & categories', 'asset.write'),
      ],
    }),
    page('content.resources', 'Resources', 'view.assets.resources', {
      children: [
        btn('content.resources.btn.write', 'Create / edit resources & categories', 'asset.write'),
      ],
    }),
    // The tenant's PUBLIC landing pages. Previously this rode
    // `view.assets.training_videos` + `asset.write`, which meant granting
    // someone Training Videos silently handed them the landing-page builder.
    // Its own view perm + its own narrow write button now.
    page('content.replicated', 'Replicated Pages', 'view.content.replicated', {
      children: [
        btn('content.replicated.btn.write', 'Create / edit pages', 'content.replicated.write'),
      ],
    }),
    page('content.announcements', 'Announcements', 'view.announcements', {
      children: [
        btn('content.announcements.btn.write', 'Create / edit announcements', 'announcement.write'),
      ],
    }),
  ]),

  // Accounting v1 — LIVE pages (Payables, Expenses, consignment reports/SOA).
  // The sample-data preview pages stay flag-gated only; these entries govern
  // the real surfaces. The whole area additionally hides while the
  // `accounting_enabled` tenant flag is off.
  section('accounting', 'Accounting', [
    // R2 — the customer master. Gated on the `view.accounting.ar` sub-leaf
    // rather than plain `view.accounting`, so a receivables clerk can be given
    // the customer list without the payables/GL surfaces. (The stockist page
    // shipped with no node here at all — do not copy that omission.)
    page('accounting.customers', 'Customers', 'view.accounting.ar', {
      children: [
        btn(
          'accounting.customers.btn.write',
          'Create / edit / deactivate customers',
          'customer.write',
        ),
        btn('accounting.customers.btn.terms', 'Manage payment terms', 'payment_terms.write'),
        // No export nodes: the page has no export affordance yet. Declaring
        // one here would advertise a capability that does not exist.
      ],
    }),
    // R2 Phase C — general billings. Same view leaf as the customer master:
    // both are the receivables desk. The two write authorities are split the
    // way consignment splits them, so a clerk can raise a billing without
    // being able to void one.
    page('accounting.ar', 'Billings (AR)', 'view.accounting.ar', {
      children: [
        btn('accounting.ar.btn.write', 'Create / edit / post billings', 'ar.invoice.write'),
        btn('accounting.ar.btn.void', 'Void billings', 'ar.void'),
      ],
    }),
    // R2 Phase D — collecting. Refunding credit is its OWN authority: an
    // operator who may record what came in is not thereby allowed to pay money
    // back out.
    page('accounting.ar_receipts', 'Payments (AR)', 'view.accounting.ar', {
      children: [
        btn(
          'accounting.ar_receipts.btn.write',
          'Record payments, apply and move customer credit',
          'ar.receipt.write',
        ),
        btn(
          'accounting.ar_receipts.btn.refund',
          'Refund customer credit',
          'ar.credit_refund.write',
        ),
        btn('accounting.ar_receipts.btn.void', 'Void payments and refunds', 'ar.void'),
      ],
    }),
    page('accounting.payables', 'Payables (supplier bills)', 'view.accounting', {
      children: [
        btn(
          'accounting.payables.btn.bill',
          'Record / void supplier bills',
          'supplier_invoice.write',
        ),
        btn('accounting.payables.btn.pay', 'Record / void bill payments', 'supplier_payment.write'),
        // Supplier-ledger drilldown exports.
        ...exportBtns('accounting.payables'),
      ],
    }),
    // Standalone RTS page (the same panel also renders on Payables). The btn
    // node keeps its historical `accounting.payables.*` key on purpose so
    // roles' stored editor state carries over — only its home moved.
    page('accounting.supplier_returns', 'Returns to Supplier (RTS)', 'view.accounting', {
      children: [
        btn(
          'accounting.payables.btn.return',
          'Record / void returns to supplier (RTS)',
          'supplier_return.write',
        ),
        // Same authority as the return it settles: recording the cash the
        // supplier sent back is not a separate decision from the return.
        btn(
          'accounting.supplier_returns.btn.refund',
          'Record / void supplier refunds (SREF)',
          'supplier_return.write',
        ),
      ],
    }),
    page('accounting.expenses', 'Expenses', 'view.accounting', {
      children: [
        btn(
          'accounting.expenses.btn.write',
          'Record / void expenses + manage categories',
          'expense.write',
        ),
        // "By category" breakdown exports.
        ...exportBtns('accounting.expenses'),
      ],
    }),
    // Cash/Bank register. Viewing balances rides `view.accounting` with the
    // rest of the area; MOVING money (manual deposits, withdrawals, transfers,
    // and voiding them) is its own treasury authority, deliberately NOT
    // implied by expense.write or supplier_payment.write.
    page('accounting.funds', 'Cash & Bank (funds register)', 'view.accounting', {
      children: [
        btn(
          'accounting.funds.btn.write',
          'Manage accounts; record and void deposits, withdrawals and transfers',
          'funds_account.write',
        ),
        // Account-register exports (the drawer's Date/Type/In/Out/Balance tape).
        ...exportBtns('accounting.funds'),
      ],
    }),
    // Lean GL (Phase 4). Both pages read on `view.accounting`; the buttons are
    // separate write authorities — a manual journal entry MOVES BOOK VALUES,
    // and a Chart-of-Accounts edit changes where every posting lands — so
    // neither is implied by the v1 accounting write perms.
    page('accounting.coa', 'Chart of Accounts', 'view.accounting', {
      children: [
        btn(
          'accounting.coa.btn.write',
          'Rename, activate and reorder Chart of Accounts entries',
          'accounting.coa.write',
        ),
      ],
    }),
    page('accounting.journal', 'General Journal', 'view.accounting', {
      children: [
        btn(
          'accounting.journal.btn.write',
          'Post manual journal entries and reverse entries',
          'accounting.journal.write',
        ),
      ],
    }),
    // Financial Statements (Trial Balance / Income Statement / Balance Sheet).
    // The route + sidebar entry have been live since Phase 4 on `view.accounting`;
    // the tree simply never listed the page, so the editor showed no row for it.
    // Read-only — its only actions are the three exports.
    page('accounting.statements', 'Financial Statements', 'view.accounting', {
      children: exportBtns('accounting.statements'),
    }),
    // Read-only admin view over the cashier-side consignment module. Its two
    // actions reuse the cashier's advisory print/export perms (same authority,
    // different surface) so unchecking them in the editor hides the buttons on
    // BOTH sides.
    page('accounting.consignment', 'Consignment (reports & SOA)', 'consignment.read', {
      children: [
        btn(
          'accounting.consignment.btn.print',
          'Print a consignee Statement of Account',
          'consignment.print',
        ),
        // Grandfathered: this surface honours `consignment.export` for CSV,
        // Excel AND PDF rather than the global `export.*` codes, so a tenant
        // that already narrowed consignment exports is not re-opened by the
        // per-format rollout.
        btn(
          'accounting.consignment.btn.export',
          'Export the outstanding-consignments report (CSV / Excel / PDF)',
          'consignment.export',
        ),
      ],
    }),
  ]),

  section('reports', 'Reports', [
    // `user.read` is for the Member Total Earned report's member picker
    // (`GET /users/search`). Not a widening in substance — `report.read` already
    // exposes every member's name, email, contact and address through
    // member-earnings / member-details.
    page('reports.page', 'Reports', 'view.reports', {
      requires: ['report.read', 'user.read'],
      // The registry-driven Reports page exports whichever report is on screen.
      children: exportBtns('reports.page'),
    }),
  ]),

  section('settings', 'Settings', [
    page('settings.system', 'System', 'view.settings.tenant', {
      // The 19 settings cards all save through one lock (tenant.features.write,
      // super-admin-only) — kept as a single authority per the product decision.
      children: [btn('settings.system.btn.write', 'Save settings', 'tenant.features.write')],
    }),
    page('settings.audit', 'Audit log', 'view.settings.audit', { requires: ['audit.read'] }),
    page('settings.dlq', 'Failed jobs (DLQ)', 'view.payouts.dlq', {
      requires: ['dlq.manage'],
      children: [btn('settings.dlq.btn.manage', 'Retry / drop jobs', 'dlq.manage')],
    }),
  ]),
];

// ── derived helpers ────────────────────────────────────────────────────────────

function walk(nodes: AccessNode[], visit: (n: AccessNode) => void): void {
  for (const n of nodes) {
    visit(n);
    if (n.children) walk(n.children, visit);
  }
}

/** Every permission referenced anywhere in the tree (perm + requires), de-duped. */
export const TREE_PERMS: string[] = (() => {
  const set = new Set<string>();
  walk(ACCESS_TREE, (n) => {
    if (n.perm) set.add(n.perm);
    for (const r of n.requires ?? []) set.add(r);
  });
  return Array.from(set);
})();

const TREE_PERM_SET = new Set(TREE_PERMS);

/** True if a permission code is surfaced by the tree (vs. the Advanced list). */
export function isTreePerm(code: string): boolean {
  return TREE_PERM_SET.has(code);
}

/**
 * Map of page-view perm → the extra read perms it auto-grants when enabled.
 * Used CLIENT-side (expand before the PUT) AND SERVER-side (re-expand in the
 * access route) so a page can never be granted without its read perms.
 */
export const PAGE_REQUIRES: Record<string, string[]> = (() => {
  const out: Record<string, string[]> = {};
  walk(ACCESS_TREE, (n) => {
    if (n.kind === 'page' && n.perm && n.requires?.length) {
      out[n.perm] = Array.from(new Set([...(out[n.perm] ?? []), ...n.requires]));
    }
  });
  return out;
})();

/**
 * Expand a set of permission codes so that whenever a page-view perm is present,
 * its `requires[]` read perms are too. Pure; returns a new sorted array.
 */
export function expandPageRequires(codes: Iterable<string>): string[] {
  const set = new Set(codes);
  for (const code of Array.from(set)) {
    for (const req of PAGE_REQUIRES[code] ?? []) set.add(req);
  }
  return Array.from(set).sort();
}

/**
 * Roles a tenant admin (non-super-admin) must never assign to a user — doing so
 * would mint another platform/tenant administrator. Mirrored server-side in
 * apps/api/src/routes/access.ts.
 */
export const PLATFORM_TIER_ROLE_CODES = ['super_admin', 'admin'] as const;

export function isPlatformTierRole(code: string): boolean {
  return (PLATFORM_TIER_ROLE_CODES as readonly string[]).includes(code);
}
